import styled from "@mui/material/styles/styled";

import PlayerControls from "@app/analytics/components/PlayerControls";
import useMeetingReportHighlights from "@app/analytics/MeetingReport/hooks/useMeetingReportHighlights";
import useTranscript from "@app/analytics/MeetingReport/hooks/useTranscript";
import useVideoStore from "@app/analytics/MeetingReport/stores/video";
import type { VideoPlayerAction } from "@app/analytics/types";

import Stack from "@rui/Stack";

import useVideoControls from "./useVideoControls";
import VideoPlayerSettings from "./VideoPlayerSettings";

const ControlsContainer = styled(Stack)({
  position: "absolute",
  borderRadius: "8px",
  padding: "8px",
  bottom: 0,
  left: 0,
  right: 0,
  transition: "all 0.5s ease",
  zIndex: 1,
});

const VideoControls = ({
  currentTimestamp,
  duration,
  hideAnnotations,
}: {
  currentTimestamp: number;
  duration: number;
  hideAnnotations: boolean;
}) => {
  const videoControls = useVideoControls();
  const { blocks, markers } = useMeetingReportHighlights();
  const { segments } = useTranscript();

  const { setVideoStartAt, setVideoReel, videoReel } = useVideoStore(
    (state) => ({
      setVideoStartAt: state.setVideoStartAt,
      setVideoReel: state.setVideoReel,
      videoReel: state.videoReel,
    })
  );

  const handleActionClick = (type: VideoPlayerAction) => {
    switch (type) {
      case "play":
        if (videoReel && videoReel.name !== "highlights") {
          setVideoReel(null);
        }
        // Clear if video reel is not the highlight reel
        // maybe there's a better way to do this, but right now
        // the behavior we want is if the highlight reel is playing
        // the play/pause should play pause the reel. Otherwise,
        // the other reels (like the participant reel) have their
        // own controls. So if a user clicks the controls under the video
        // player, we assume they want to play the video and not the reel
        return videoControls.play();
      case "pause":
        return videoControls.pause();
      case "mute":
        return videoControls.mute();
      case "unmute":
        return videoControls.unmute();
      case "fullScreen":
        return videoControls.fullScreen();
      case "nextHighlight":
        return videoControls.goToNextHighlight();
      default:
        return;
    }
  };

  return (
    <ControlsContainer className="video-controls">
      <PlayerControls
        additionalActions={
          videoControls.fullScreenEnabled ? [{ action: "fullScreen" }] : []
        }
        blocks={blocks}
        currentTime={currentTimestamp}
        duration={duration}
        markers={markers}
        muted={videoControls.muted}
        paused={videoControls.paused}
        onActionClick={handleActionClick}
        onClickBlockOrMarker={setVideoStartAt}
        onScrubberChange={videoControls.jumpToTimestamp}
        segments={segments}
        renderSettings={VideoPlayerSettings}
        settingsTargetClassName="meeting-report__video-container"
        withAnnotations={!hideAnnotations}
      />
    </ControlsContainer>
  );
};

export default VideoControls;
